/**
 * Invoice-day date rules, ported from old CMT `utilityService.js`
 * ([e8410a8](https://bitbucket.org/egroupio/customer-management-tool/src/e8410a8/app/scripts/services/utilityService.js)).
 *
 * Three calendar-highlight helpers used to live here — `dayAfterInvoiceDay`,
 * `isDayAfterInvoiceDay` and `isInvoiceDay`. They were removed in the PR #42 review
 * (roundup #6): none of them ever had a production caller — introduced dead in EB-3529 and
 * dead ever since — and `dayAfterInvoiceDay` had drifted from its legacy original while its
 * own spec certified the drift as intended. Legacy `dayAfterInvoice`
 * ([utilityService.js#lines-48:53](https://bitbucket.org/egroupio/customer-management-tool/src/e8410a8/app/scripts/services/utilityService.js#lines-48:53))
 * wraps only at `=== 31`; the port wrapped at `>= 28`, so invoice days 28/29/30 answered 1
 * where old CMT answers 29/30/31. Unused parity helpers drift silently and their tests then
 * certify the drift — re-derive from the legacy source above if a calendar highlight is ever
 * built.
 */

/**
 * Old CMT `utilityService.getDefaultEndDate`
 * ([utilityService.js#lines-73:80](https://bitbucket.org/egroupio/customer-management-tool/src/e8410a8/app/scripts/services/utilityService.js#lines-73:80)):
 *
 * ```js
 * var defaultDate = moment();
 * if (defaultDate.date() > invoiceDay) {   // NOTE: no -1, unlike getDefaultStartDate
 *     defaultDate.add(1, 'months');
 * }
 * defaultDate.date(invoiceDay);
 * ```
 *
 * i.e. the next occurrence of the invoice day — this month while today is still on or
 * before it, the next month afterwards. Default for the cancellation date. Old CMT also
 * prefills the end-contract and remove-extension dates from it
 * (`contractRevisions.js:175-176`); those AMIE dialogs do not use it yet.
 *
 * The asymmetry against {@link defaultStartDate} — `> invoiceDay` here, `> invoiceDay - 1`
 * there — is **old CMT's and intentional**. On invoice day 20, today the 20th, this gives
 * 20 Jan while the start date gives 21 Feb. Do not "harmonise" the two: the conditionals
 * are quoted above and in {@link defaultStartDate} precisely so the next reader can check
 * them without the legacy repo to hand (PR #42 review, roundup #5; Rule 24).
 *
 * The date is built with the `new Date(y, m, d)` constructor so the month rolls
 * forward without the intermediate-invalid-date overflow of `setMonth` (31 Mar
 * with setMonth would skip to May).
 */
export function defaultEndDate(invoiceDay: number, today: Date = new Date()): Date {
  const monthOffset = today.getDate() > invoiceDay ? 1 : 0;
  return new Date(today.getFullYear(), today.getMonth() + monthOffset, invoiceDay);
}

/**
 * Old CMT `utilityService.getDefaultStartDate`
 * ([utilityService.js#lines-87:94](https://bitbucket.org/egroupio/customer-management-tool/src/e8410a8/app/scripts/services/utilityService.js#lines-87:94)):
 *
 * ```js
 * var defaultDate = moment();
 * if (defaultDate.date() > (invoiceDay - 1)) {   // NOTE: the -1 is deliberate
 *     defaultDate.add(1, 'months');
 * }
 * defaultDate.date(invoiceDay + 1);
 * ```
 *
 * i.e. the day after the next invoice day — the first day of the following billing
 * period. Default for everything that *starts* a new billing arrangement: plan change,
 * plan discount, "Send to Basic".
 *
 * Both halves matter and the earlier docstring quoted only the second, which made the
 * function uncheckable and read as a different algorithm from the code (roundup #5). The
 * `- 1` is why this and {@link defaultEndDate} disagree on the invoice day itself; see
 * there.
 *
 * `invoiceDay + 1` can exceed the month, and `new Date(y, m, 32)` then rolls forward the
 * way moment's `.date(32)` does. It does **not** always land on the 1st, which this
 * docstring used to claim (review roundup #6): that holds only in a 31-day month.
 * `new Date(2025, m, 32)` gives 1 Feb from January, **4 Mar** from February, and the **2nd**
 * from every 30-day month. The spec's only overflow case was January — the one month where
 * the old sentence was true.
 *
 * Why collapsing legacy's two steps into one constructor call is equivalent rather than
 * merely close, since this is the objection the port invites: legacy adds the month
 * **first** (`moment.add(1, 'months')`, which clamps 31 Jan → 28 Feb) and only then
 * overwrites the day with `.date(invoiceDay + 1)`. The clamp can therefore never survive
 * into the result — the day is always overwritten afterwards — so `new Date(y, m + offset,
 * invoiceDay + 1)` produces the same date. Verified by Jana Reznickova across invoice days
 * 1-31 × every calendar day of 2025 (11,315 pairs, zero divergences).
 */
export function defaultStartDate(invoiceDay: number, today: Date = new Date()): Date {
  const monthOffset = today.getDate() > invoiceDay - 1 ? 1 : 0;
  return new Date(today.getFullYear(), today.getMonth() + monthOffset, invoiceDay + 1);
}
